// Kursweite Vorschau-Sperre.
//
// Hintergrund: `course_lessons.is_preview` wurde bis 23.09.2026 NIRGENDS in der
// Zugriffsentscheidung abgefragt — weder in der RLS-Regel (die prüft nur
// `is_published`) noch in `canAccessLesson`. Das Häkchen im Admin steuerte
// allein die Anzeige.
//
// Das Feld global scharf zu schalten war keine Option: 251 Lektionen in 95
// Kursen haben es gesetzt. Deshalb entscheidet der Schalter
// `courses.preview_lessons_public` PRO KURS, ob `is_preview` überhaupt
// Wirkung hat.
//
// Ist der Schalter gesetzt, gilt in diesem Kurs:
//   Admin / Course-Admin           -> alles (normale Regeln)
//   Mitglied (pro)                 -> alles (normale Regeln)
//   eingeschrieben, kein Mitglied  -> NUR Lektionen mit `is_preview`
//   nicht eingeschrieben           -> unverändert (normale Regeln)
//
// Für eingeschriebene Nicht-Mitglieder ist das eine VERENGUNG: eine
// kostenlose Einschreibung gewährt über `isFullAccessSource` sonst alles.
// Genau deshalb gilt die Sperre nur in Kursen mit gesetztem Schalter.
//
// Nicht eingeschriebene Nutzer bleiben bewusst außen vor: Die Einschreibung
// ist der Weg, aus Newsletter-Kontakten echte Plattform-Accounts zu machen.
// Wären die Aufzeichnungen ohne Einschreibung sichtbar, fiele dieser Schritt
// weg.

export interface VorschauKontext {
  /** Trägt der Kurs `preview_lessons_public`? */
  kursHatVorschauSchalter: boolean;
  tier: "start" | "pro";
  isAdmin: boolean;
  /** Course-Admin für genau diesen Kurs (Vorschau-/Bearbeitungsrecht). */
  istVerwalteterKurs: boolean;
  /** Ist der Nutzer in diesem Kurs eingeschrieben (beliebige Quelle)? */
  istEingeschrieben: boolean;
}

/**
 * Greift die Sperre für diesen Nutzer in diesem Kurs?
 *
 * Nur wenn `true`, entscheidet `is_preview` über den Lektionszugriff. Sonst
 * gelten die normalen Regeln unverändert.
 */
export function vorschauSperreGreift(k: VorschauKontext): boolean {
  if (!k.kursHatVorschauSchalter) return false;
  if (k.isAdmin || k.istVerwalteterKurs) return false;
  if (k.tier === "pro") return false;
  return k.istEingeschrieben;
}

/**
 * Darf der Nutzer diese Lektion sehen, wenn die Sperre greift?
 *
 * Gibt `null` zurück, wenn die Sperre nicht greift — dann muss der Aufrufer
 * mit den normalen Regeln weitermachen.
 */
export function vorschauErlaubtLektion(
  k: VorschauKontext,
  lektionIstVorschau: boolean,
): boolean | null {
  if (!vorschauSperreGreift(k)) return null;
  return lektionIstVorschau;
}

/**
 * Entzieht die Sperre den „vollen Kurszugriff"?
 *
 * `hasFullAccessToCourse` wird an mehreren Stellen als Abkürzung benutzt, die
 * die Lektionsprüfung überspringt. In einem Kurs mit gesetztem Schalter darf
 * ein Nicht-Mitglied diese Abkürzung nicht bekommen, sonst wäre die Sperre
 * wirkungslos. Die Einschreibung spielt hier keine Rolle: Ohne sie hat der
 * Nutzer ohnehin keinen vollen Zugriff.
 */
export function vorschauEntziehtVollzugriff(
  k: Omit<VorschauKontext, "istEingeschrieben">,
): boolean {
  if (!k.kursHatVorschauSchalter) return false;
  if (k.isAdmin || k.istVerwalteterKurs) return false;
  return k.tier !== "pro";
}
